/* ---------------------------------------------------------------------------
   The damage inputs a rating takes from the inspection. LIFECYCLE.md `LC-20`
   and `LC-21`.

   Section loss, stirrup loss and crack depth are what an inspection finds, not
   what a drawing states. Until this file they defaulted to zero — the
   undamaged as-built section, marked `standard` — and a rating could be
   accepted that way while the register held a measured 38.9% loss of the very
   girder being rated. Now the form is filled from the open defects on the span
   and girder being rated, each value marked `inspection` and naming the defect
   it came from, against the latest accepted inspection. And a rating whose
   damage inputs are still the standard's zeros cannot be accepted, unless the
   engineer records *inspected, no loss found* against that inspection.

   ONE FILE, BOTH SIDES. The form fills from it, the server re-derives the fill
   from the stored defects before it keeps a rating that claims one, and the
   acceptance check is the same function on both.

   HOW A DEFECT IS PLACED ON A SPAN AND GIRDER — and where that stops. A defect
   records an element (DoR's coarse inventory item) and a component label in
   the modeller's naming, "Span-2 Girder G1 Soffit". The label is the only
   place the span and the girder are written, so they are read out of it:
   `Span-<n>` is the span, `Girder G<n>` or `G<n>` the girder, and G1 and the
   last girder are the exteriors. What that cannot do, and is not asked to:
     · a label with no span counts against every span, and one with no girder
       against both positions — the worse reading, stated in a note;
     · a girder number past G1 is placed only where the girder count is known;
     · the pin (a point in world metres) is not mapped to a span, because this
       file has no geometry to map it against;
     · no defect category records a crack's DEPTH (QUANTITIES.md: a crack has a
       length and a width), so shear crack depth is never filled from a defect.
       It is entered as measured, or recorded as inspected with no loss.
   LC-14 stores the model's component id BESIDE the label, and both are read.
   An imported model's labels are its own short names ("G-3"), which say the
   girder but not the span, while its ids are paths that say both
   ("Span-2/Girder-3"). On the 2026-09-30 QA run a girder defect with 12 %
   section loss, stored as G-3 on Span-2/Girder-3, reached no rating at all.
   So span and girder are read from the label and the id together, and "G-3"
   reads as girder 3 the way "G3" always did.
   --------------------------------------------------------------------------- */

import { isOpenStatus } from '../data/defects.ts';
import { isGirderKind, isSlabKind, type ComponentPlace } from '../data/modelComponents.ts';
import type { Bridge, Inspection, RegisterDefect } from '../data/types.ts';

/** The families whose damage inputs this knows. */
export type DamageFamily = 'RCFS' | 'RCDG' | 'PCDG';

/** The basis value for *inspected, no loss found*, recorded against the inspection. */
export const NO_LOSS = 'no-loss';

/** The damage inputs a family's form asks for, in the form's own order. */
export function damageKeys(family: string): readonly string[] {
  if (family === 'PCDG') return ['prestress.sectionLoss', 'stirrupSectionLoss', 'shearCrackDepth'];
  if (family === 'RCDG') return ['sectionLoss', 'stirrupSectionLoss', 'shearCrackDepth'];
  return ['sectionLoss'];
}

export const DAMAGE_LABEL: Readonly<Record<string, string>> = {
  sectionLoss: 'Section loss, main steel',
  'prestress.sectionLoss': 'Tendon section loss',
  stirrupSectionLoss: 'Stirrup section loss',
  shearCrackDepth: 'Shear crack depth',
};

/* --------------------------- where a defect is --------------------------- */

export interface DefectPlace {
  /** The span its label names, or null. */
  span: number | null;
  /** The girder number its label names, or null. */
  girder: number | null;
  /** Interior or exterior, where the girder and the count say which. */
  position: 'interior' | 'exterior' | null;
  /** Whether it is on the member a superstructure rating rates. */
  superstructure: boolean;
  /** Whether it is the shear reinforcement rather than the main steel. */
  stirrups: boolean;
}

/** Labels that name something other than the rated member. */
const NOT_RATED = /surface|barrier|parapet|rail|approach|joint|bearing|pier|abutment|bent|\bcap\b|pile|footing|wing|drain/i;
/** Labels that name the rated member where no element was recorded. */
const RATED = /girder|beam|slab|deck|soffit|web|stringer|tendon|strand/i;

export function placeOfDefect(
  d: Pick<RegisterDefect, 'element' | 'component' | 'note'> & { componentId?: string | null; place?: ComponentPlace },
  girderCount: number | null,
): DefectPlace {
  /* WHERE THE MODEL SAYS (migration 0049): the component's own place, from the
     model's directory — span, girder of how many. Read before the label, which
     is the fallback for a defect with no component the directory knows. */
  if (d.place) {
    const p = d.place;
    const girder = isGirderKind(p.kind);
    const n = girder ? p.index : null;
    const count = p.count ?? girderCount;
    return {
      span: p.span,
      girder: n,
      position:
        n === null ? null : n === 1 || (count !== null && n === count) ? 'exterior' : count !== null ? 'interior' : null,
      superstructure: girder || isSlabKind(p.kind),
      stirrups: /stirrup|\blinks?\b|shear reinf/i.test(`${d.component ?? ''} ${d.note}`),
    };
  }
  /* The id's steps as words: "Span-2/Girder-3" reads "Span-2 Girder-3". A GUID
     says nothing and matches nothing, which is the same as no id. */
  const label = [d.component ?? '', (d.componentId ?? '').replace(/\//g, ' ')].join(' ').trim();
  const span = /\bspan[\s-]*(\d+)/i.exec(label);
  const girder = /\bgirder[\s-]*G?(\d+)\b/i.exec(label) ?? /\bG-?(\d+)\b/.exec(label);
  const n = girder ? Number(girder[1]) : null;
  const position =
    n === null
      ? null
      : n === 1 || (girderCount !== null && n === girderCount)
        ? 'exterior'
        : girderCount !== null && n > 1 && n < girderCount
          ? 'interior'
          : null;
  return {
    span: span ? Number(span[1]) : null,
    girder: n,
    position,
    superstructure: !NOT_RATED.test(label) && (d.element === 'deck' || (d.element == null && RATED.test(label))),
    stirrups: /stirrup|\blinks?\b|shear reinf/i.test(`${label} ${d.note}`),
  };
}

/**
 * Whether a defect is still open: not resolved, repaired or verified (LC-62).
 * A repaired member's loss is the repair's to restore, not the rating's to
 * carry.
 */
export function isOpenDefect(d: Pick<RegisterDefect, 'status'>): boolean {
  return isOpenStatus(d.status);
}

/**
 * What a defect measures now: the latest later visit that measured it, else
 * the finding (LC-62). Growth between visits reaches the rating this way; the
 * finding itself is never rewritten.
 */
export function currentQuantity(d: Pick<RegisterDefect, 'quantity' | 'lastSeen'>): RegisterDefect['quantity'] {
  return d.lastSeen?.quantity ?? d.quantity;
}

/**
 * Whether a defect is on the member a rating of this span and girder rates. A
 * side that does not say matches — the worse reading.
 */
export function onRatedMember(
  place: DefectPlace,
  family: string,
  spanIndex: number | null,
  position: string | null,
): boolean {
  if (!place.superstructure) return false;
  if (spanIndex !== null && place.span !== null && place.span !== spanIndex) return false;
  if (family !== 'RCFS' && position !== null && place.position !== null && place.position !== position) return false;
  return true;
}

/* --------------------------- the inspection --------------------------- */

const DETAILED: ReadonlySet<string> = new Set(['principal', 'baseline', 'special', 'post-event', 'underwater']);

/**
 * The latest accepted inspection — the one a rating is made against. On one
 * day, a principal or special inspection is taken over the routine walk.
 */
export function latestAcceptedInspection(inspections: readonly Inspection[]): Inspection | null {
  let best: Inspection | null = null;
  for (const i of inspections) {
    if (i.state !== 'accepted' || !i.id) continue;
    if (
      best === null ||
      i.date > best.date ||
      (i.date === best.date && DETAILED.has(i.type) && !DETAILED.has(best.type))
    ) {
      best = i;
    }
  }
  return best;
}

/* --------------------------- the fill --------------------------- */

export interface DamageRequest {
  family: string;
  position: string | null;
  spanIndex: number | null;
  /** From the form where it holds one; places a girder past G1. */
  girderCount: number | null;
}

export interface DamageFill {
  /** The accepted inspection the fill is made against, or null where there is none. */
  inspectionId: string | null;
  inspectionDate: string | null;
  /** Damage field to value, in the form's units. */
  fields: Record<string, number>;
  /** Damage field to the defect id it came from. */
  basis: Record<string, string>;
  /** Every defect whose measurement was used, once. */
  defectIds: string[];
  /** What was read, and what could not be — one sentence each. */
  notes: string[];
}

/**
 * The damage inputs this bridge's open defects give a rating of one span and
 * girder. The worst measurement on the member wins each field.
 */
export function damageFromInspection(
  bridge: Pick<Bridge, 'id' | 'inspections'>,
  defects: readonly RegisterDefect[],
  request: DamageRequest,
): DamageFill {
  const inspection = latestAcceptedInspection(bridge.inspections);
  const keys = damageKeys(request.family);
  const main = keys[0]!;
  const fields: Record<string, number> = {};
  const basis: Record<string, string> = {};
  const notes: string[] = [];

  for (const d of defects) {
    if (d.bridgeId !== bridge.id || !isOpenDefect(d)) continue;
    const loss = currentQuantity(d)?.sectionLossPct;
    if (typeof loss !== 'number' || !Number.isFinite(loss) || loss < 0) continue;
    const place = placeOfDefect(d, request.girderCount);
    if (!onRatedMember(place, request.family, request.spanIndex, request.position)) continue;
    const key = place.stirrups && keys.includes('stirrupSectionLoss') ? 'stirrupSectionLoss' : main;
    const where = d.component ?? d.element ?? 'no location';
    const caveats = [
      place.span === null ? 'its label names no span, so it counts against every span' : null,
      request.family !== 'RCFS' && place.position === null
        ? 'its label does not say which girder, so it counts against both'
        : null,
    ].filter(Boolean);
    notes.push(
      `${d.id} (${where}): ${loss}% section loss → ${DAMAGE_LABEL[key]}${caveats.length ? ` — ${caveats.join('; ')}` : ''}.`,
    );
    if (fields[key] === undefined || loss > fields[key]!) {
      fields[key] = loss;
      basis[key] = d.id;
    }
  }

  if (keys.includes('shearCrackDepth')) notes.push('No issue records a crack’s depth.');

  return {
    inspectionId: inspection?.id ?? null,
    inspectionDate: inspection?.date ?? null,
    fields,
    basis,
    defectIds: [...new Set(Object.values(basis))],
    notes,
  };
}

/* --------------------------- acceptance --------------------------- */

/**
 * The damage inputs still assumed: marked `standard` or unattributed, or
 * claiming the inspection without saying which defect, or *no loss found*
 * without an inspection to have found it in. LC-21 refuses acceptance while
 * this is not empty.
 */
export function assumedDamage(
  family: string,
  sources: Readonly<Record<string, string>>,
  basis: Readonly<Record<string, string>>,
  inspectionId: string | null,
): string[] {
  return damageKeys(family).filter((key) => {
    const tier = sources[key];
    if (tier === undefined || tier === 'standard') return true;
    if (tier !== 'inspection') return false;
    const why = basis[key];
    if (why === undefined) return true;
    return why === NO_LOSS && inspectionId === null;
  });
}

/** One sentence naming what LC-21 still refuses on, or null where nothing. */
export function assumedDamageLine(keys: readonly string[]): string | null {
  if (keys.length === 0) return null;
  return `Still assumed, not inspected: ${keys.map((k) => DAMAGE_LABEL[k] ?? k).join(', ')}.`;
}

/**
 * Why the deployment would refuse to save this damage basis, or null (QA
 * 2026-09-30). The same two rules functions/lib/ratings.ts `checkDamage` states
 * first, so Save is not offered for a form the server answers with a 400: a
 * value marked as from the inspection must name the defect, or the no-loss
 * finding, it came from — which the fill from the inspection supplies and a
 * source picked by hand does not.
 */
export function unsavableDamage(
  sources: Readonly<Record<string, string>>,
  basis: Readonly<Record<string, string>>,
): string | null {
  for (const [field, tier] of Object.entries(sources)) {
    if (tier === 'inspection' && basis[field] === undefined) {
      return `${DAMAGE_LABEL[field] ?? field} is marked as from the inspection but names no issue.`;
    }
  }
  return null;
}
